import { Injectable } from '@angular/core';
import { AppStateService } from 'core/app-state/app-state.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { LoggerService } from '../logger/logger.service';

@Injectable()
export class SessionService {
  private currentIdleSeconds = 0;
  private timeoutAfterSeconds: number;
  private idleCheckIntervalSeconds: number;
  private renewInterval: number;
  private tokenRenewBeforeSeconds: number;
  private times = {
    resetIdle: null,
    sessionChecked: null
  };

  constructor(
    private authenticationService: AuthenticationService,
    private loggerService: LoggerService,
    private appStateService: AppStateService
  ) { }

  /**
   * Validates configuration
   */
  private validateConfig() {
    if (this.timeoutAfterSeconds / this.idleCheckIntervalSeconds < 3) {
      throw new Error('Error! Session configuration: Timeout period must be at least 3 times bigger than the idle check interval.');
    }

    if (this.timeoutAfterSeconds < 60) {
      throw new Error('Error! Session configuration: Timeout period must be at least 60 seconds.');
    }
  }

  /**
   * Sets up session monitoring
   * @param timeoutAfterSeconds - The period of inactivity after which the user is logged out
   * @param idleCheckIntervalSeconds - The interval to check token and user inactivity
   */
  setupSessionMonitoring(
    timeoutAfterSeconds: number,
    idleCheckIntervalSeconds: number
  ) {
    this.timeoutAfterSeconds = timeoutAfterSeconds;
    this.idleCheckIntervalSeconds = idleCheckIntervalSeconds;
    this.tokenRenewBeforeSeconds = timeoutAfterSeconds / 2; // renew half-time before session expiration
    this.validateConfig();

    // bind events for idle time reset
    window.document.addEventListener('click', () => this.resetIdleTimer());
    window.document.addEventListener('keypress', () => this.resetIdleTimer());
    window.document.addEventListener('touchstart', () => this.resetIdleTimer());

    // setup the renew interval
    this.renewInterval = window.setInterval(() => this.checkSession(), this.idleCheckIntervalSeconds * 1000);
  }

  /**
   * Resets idle timer
   */
  private resetIdleTimer() {
    this.currentIdleSeconds = 0;
    this.times.resetIdle = new Date();
  }

  /**
   * Checks idle time and renews session if necessary
   */
  private checkSession() {
    this.times.sessionChecked = new Date();

    if (this.times.resetIdle) {
      this.currentIdleSeconds = (this.times.sessionChecked.getTime() - this.times.resetIdle.getTime()) / 1000;
    } else {
      this.currentIdleSeconds += this.idleCheckIntervalSeconds;
    }

    // eslint-disable-next-line max-len
    const loggedOut = `UI_SESSION: User was logged out due to inactivity after: ${this.currentIdleSeconds}s. Timeout is set to: ${this.timeoutAfterSeconds}s.`;
    // check if session expired
    if (this.timeoutAfterSeconds - this.currentIdleSeconds <= 0) {
      if (this.appStateService.state.isElectron && this.appStateService.receiveOnlineUpdatesInProgress) {
        // We're on Electron and still syncing, do NOT logout automatically
        return;
      }
      this.loggerService.logInfo(loggedOut);
      window.clearInterval(this.renewInterval);
      return this.authenticationService.expireSession();
    }

    // get existing token
    const existingToken = this.authenticationService.existingToken;
    if (!existingToken) {
      this.loggerService.logError('UI_SESSION: User was logged out due to missing token.');
      window.clearInterval(this.renewInterval);
      return this.authenticationService.logout();
    }

    // check if token should be renewed (will be renewed if token will expire in less than five times the idle check interval)
    const dateDiff = (existingToken.expires.getTime() - new Date().getTime()) / 1000;
    if (dateDiff >= 0 && dateDiff < this.tokenRenewBeforeSeconds) {
      this.loggerService.logInfo(`UI_SESSION: Attempting to renew token. Existing token expires in: ${dateDiff}s.`);
      this.authenticationService.renewToken();
    }
  }
}
